import React from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {Button, Modal, ModalActions, ModalHeader, showModal} from '@edna/components';

import {CollapsibleSection, TBadgeColor} from 'src/components';
import {LeftButton, SecondaryText} from 'src/components/primitives';
import {useGetAllSubjectsQuery} from 'src/containers/Subjects/api';

import {
  useAgreeTariffChangeRequestMutation,
  useDisagreeTariffChangeRequestMutation,
  useGetNeedToAgreeTariffChangeRequestsQuery,
} from '../api';
import {ETariffChangeRequestStatus, TTariffChangeRequest} from '../definitions';
import * as S from './style';

const tariffChangeRequestColorHash: Record<ETariffChangeRequestStatus, TBadgeColor> = {
  [ETariffChangeRequestStatus.AGREED]: 'GREEN',
  [ETariffChangeRequestStatus.NEED_TO_AGREE]: 'GRAY',
  [ETariffChangeRequestStatus.PROCESSED]: 'GRAY',
  [ETariffChangeRequestStatus.CANCELED]: 'RED',
  [ETariffChangeRequestStatus.REJECTED]: 'RED',
  [ETariffChangeRequestStatus.NOT_AGREED]: 'RED',
};

const TariffChangeRequest = React.memo<{
  tariffChangeRequest: TTariffChangeRequest;
  agree: (id: TTariffChangeRequest['id']) => void;
  disagree: (id: TTariffChangeRequest['id']) => void;
}>(
  ({
    tariffChangeRequest: {id, subjectId, newTenantTariffName, newTenantTariffDescription, status},
    agree,
    disagree,
  }) => {
    const {t} = useTranslation();

    const {subjectName} = useGetAllSubjectsQuery(
      {testing: false},
      {
        selectFromResult: ({data}) => ({
          subjectName: data?.find((subject) => subject.id === subjectId)?.name,
        }),
      },
    );

    const handleAgree = React.useCallback(() => {
      agree(id);
    }, [agree, id]);

    const handleDisagree = React.useCallback(() => {
      disagree(id);
    }, [disagree, id]);

    const hasNeedToAgreeStatus = status === ETariffChangeRequestStatus.NEED_TO_AGREE;

    return (
      <CollapsibleSection
        isOpenDefault
        size="L"
        label={
          <div>
            {!hasNeedToAgreeStatus && (
              <S.Badge color={tariffChangeRequestColorHash[status]}>
                {t(`Tariffs:status.${status}`)}
              </S.Badge>
            )}
            <div>
              <Trans
                i18nKey="Tariffs:agreeTariffChange.text"
                values={{newTariffName: newTenantTariffName, subjectName}}
              >
                <SecondaryText as="span" />
              </Trans>
            </div>
          </div>
        }
      >
        <S.Description>{newTenantTariffDescription}</S.Description>
        {hasNeedToAgreeStatus && (
          <ModalActions>
            <LeftButton
              appearance="secondary"
              onClick={handleDisagree}
              data-selector="Tariffs:button.refuse"
            >
              {t('Tariffs:button.refuse')}
            </LeftButton>
            <Button
              appearance="primary"
              onClick={handleAgree}
              data-selector="Tariffs:button.agreeTariff"
            >
              {t('Tariffs:button.agreeTariff')}
            </Button>
          </ModalActions>
        )}
      </CollapsibleSection>
    );
  },
);

const AgreeTariffChangeModal = React.memo<{
  closeModal: TAnyFunction;
}>(({closeModal}) => {
  const {t} = useTranslation();

  const {data: tariffChangeRequests = []} = useGetNeedToAgreeTariffChangeRequestsQuery();
  const [agreeTariffChange] = useAgreeTariffChangeRequestMutation();
  const [disagreeTariffChange] = useDisagreeTariffChangeRequestMutation();

  React.useEffect(() => {
    const needToAgreeRequestCount = tariffChangeRequests.filter(
      ({status}) => status === ETariffChangeRequestStatus.NEED_TO_AGREE,
    );

    if (!needToAgreeRequestCount) {
      closeModal();
    }
  }, [tariffChangeRequests, closeModal]);

  return (
    <Modal width="582px">
      <ModalHeader>
        {t('Tariffs:agreeTariffChange.title', {
          count: tariffChangeRequests.length,
        })}
      </ModalHeader>
      <SecondaryText bottomIndent="6">{t('Tariffs:agreeTariffChange.subtitle')}</SecondaryText>
      {tariffChangeRequests.map((tariffChangeRequest) => (
        <TariffChangeRequest
          key={tariffChangeRequest.id}
          tariffChangeRequest={tariffChangeRequest}
          agree={agreeTariffChange}
          disagree={disagreeTariffChange}
        />
      ))}
    </Modal>
  );
});

export const showAgreeTariffChangeModal = () =>
  showModal((closeModal) => <AgreeTariffChangeModal closeModal={closeModal} />);
